/**
 * 上课记录服务 (考勤)
 * 处理上课记录相关的 API 调用
 * 
 * 注意: 前端称为"上课记录"，后端 API 使用 attendance-records
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * 上课记录服务
 */
const attendanceService = {
  /**
   * 获取上课记录列表（分页）
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.per_page - 每页数量
   * @param {string} params.student_name - 学员姓名筛选
   * @param {string} params.course_name - 课程名筛选
   * @param {number} params.course_id - 课程 ID 筛选
   * @param {string} params.teacher - 教师筛选
   * @param {string} params.status - 状态筛选 (PENDING/CONFIRMED/CANCELLED)
   * @returns {Promise<Object>}
   */
  async getList(params = {}) {
    return apiClient.get(API_ENDPOINTS.ATTENDANCE.LIST, {
      page: 1,
      per_page: 20,
      ...params,
    });
  },

  /**
   * 获取上课记录详情
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    return apiClient.get(API_ENDPOINTS.ATTENDANCE.DETAIL(id));
  },

  /**
   * 创建上课记录
   * @param {Object} data - 记录数据
   * @param {number} data.student_id - 学员 ID
   * @param {number} data.course_id - 课程 ID
   * @param {string} data.date - 上课日期 (YYYY-MM-DD)
   * @param {string} data.status - 状态
   * @param {string} data.remarks - 备注
   * @param {boolean} [data.use_reward_units] - 是否使用赠课抵扣（可选）
   * @returns {Promise<Object>}
   */
  async create(data) {
    return apiClient.post(API_ENDPOINTS.ATTENDANCE.CREATE, data);
  },

  /**
   * 更新上课记录
   * @param {number} id - 记录 ID
   * @param {Object} data - 更新数据
   * @returns {Promise<Object>}
   */
  async update(id, data) {
    return apiClient.put(API_ENDPOINTS.ATTENDANCE.UPDATE(id), data);
  },

  /**
   * 删除上课记录
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async delete(id) {
    return apiClient.delete(API_ENDPOINTS.ATTENDANCE.DELETE(id));
  },

  /**
   * 作废上课记录（回退课时和金额）
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async void(id) {
    return apiClient.post(API_ENDPOINTS.ATTENDANCE.VOID(id));
  },

  /**
   * 确认上课记录
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async confirm(id) {
    return this.update(id, { status: 'CONFIRMED' });
  },

  /**
   * 取消上课记录
   * @param {number} id - 记录 ID
   * @returns {Promise<Object>}
   */
  async cancel(id) {
    return this.update(id, { status: 'CANCELLED' });
  },
};

export default attendanceService;
